import { FC, useMemo, useState } from "react";
import Switch from "../../../../component/switch";
import WriteIcon from "../../../../icons/write";
import { ExamDetail, ExamMode } from "../../../../types/exam";
import { useUserConfig } from "../store";
import styles from "./index.module.scss";
import cn from "classnames";
import { Button, Loading, Modal } from "@quark/design";
import { startExam, submitExam } from "../../../../api";
import { useNavigate } from "react-router-dom";
import FileIcon from "../../../../icons/file";
import LogListModal from "../log-list-modal";

interface RightPanelProps {
  currentIndex: number;
  exam: ExamDetail;
  mode: ExamMode;
  onModeChange: (mode: ExamMode) => void;
  onIndexChange: (indexx: number) => void;
  onSubmit: () => void;
}

const RightPanel: FC<RightPanelProps> = ({
  currentIndex,
  exam,
  mode,
  onModeChange,
  onIndexChange,
  onSubmit,
}) => {
  const navigate = useNavigate();
  const { config, updateConfig } = useUserConfig();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [logListVisible, setLogListVisible] = useState(false);
  const [redoLoading, setRedoLoading] = useState(false);

  const { log, id } = exam;
  const { right_num = 0, wrong_num = 0, id: logId } = log || { id: 0 };
  const hasFinished = useMemo(() => {
    return exam.quiz_num === right_num + wrong_num;
  }, [exam, right_num, wrong_num]);

  const hasSubmitted = useMemo(() => {
    return exam.log?.status === "done";
  }, [exam]);

  const progress = useMemo(() => {
    const { log } = exam || {};
    if (log) {
      return log.right_num + log.wrong_num;
    }
    return 0;
  }, [exam]);

  const switchValueChange = (val: boolean) => {
    setLoading(true);
    updateConfig({
      ...config,
      exam: {
        ...config.exam,
        auto_next: val,
      },
    }).finally(() => {
      setLoading(false);
    });
  };

  const submit = () => {
    if (submitting) return;
    setSubmitting(true);
    submitExam(id, logId)
      .then(() => {
        onSubmit();
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const redoExam = () => {
    if (redoLoading) return;
    setRedoLoading(true);
    startExam(exam.id)
      .then((res) => {
        navigate(`/exam/${exam.id}/${res.id}`);
      })
      .finally(() => {
        setRedoLoading(false);
      });
  };

  return (
    <>
      <div className={styles.card}>
        <div className={styles.title}>{exam.title}</div>

        <div className={styles.info}>
          {hasSubmitted ? (
            <div className={styles.left}>
              正确
              <span style={{ color: "#40BF9C" }}>{exam.log?.right_num}</span>题,
              错误
              <span style={{ color: "#f26a58" }}>{exam.log?.wrong_num}</span>题,
              正确率:&nbsp;
              {Math.floor(
                ((exam.log?.right_num || 0) / (exam.log?.quiz_num || 1)) * 100
              )}
              %
            </div>
          ) : (
            <div className={styles.left}>
              已答:&nbsp;<span style={{ color: "#40BF9C" }}>{progress}</span>/
              {exam.log?.quiz_num || 0}题
            </div>
          )}

          <div className={styles.right} onClick={() => setLogListVisible(true)}>
            做题记录
            <FileIcon />
          </div>
        </div>
      </div>

      <div className={styles.card}>
        <div className={styles.switchLine}>
          <Switch
            value={mode === "recite"}
            onChange={() => {
              if (mode === "recite") {
                onModeChange("practice");
              } else {
                onModeChange("recite");
              }
            }}
          />{" "}
          背题模式
        </div>
        <div className={styles.switchLine}>
          <Switch
            value={config.exam.auto_next}
            loading={loading}
            onChange={switchValueChange}
          />{" "}
          答对自动下一题
        </div>

        <div className={styles.listTitle}>
          <WriteIcon />
          答题卡
        </div>

        <div className={styles.list}>
          {exam.quizs.map((quiz, index) => {
            const answer = quiz?.user_answers?.[0];
            const isRight = answer && answer.is_right;
            const isWrong = answer && !answer.is_right;
            return (
              <div
                className={cn(
                  styles.item,
                  isRight && styles.right,
                  isWrong && styles.wrong,
                  currentIndex === index && styles.active
                )}
                key={quiz.question_id}
                onClick={() => onIndexChange(index)}
              >
                {index + 1}
              </div>
            );
          })}
        </div>
      </div>

      {!hasSubmitted ? (
        <div className={styles.actions}>
          <Button
            width="100%"
            variant="filled"
            onClick={() => {
              if (hasFinished) {
                submit();
              } else {
                setShowWarning(true);
              }
            }}
          >
            {submitting ? <Loading /> : "交卷"}
          </Button>
        </div>
      ) : (
        <div className={styles.actions}>
          <Button width="100%" variant="filled" onClick={redoExam}>
            {redoLoading ? <Loading /> : "再刷一次"}
          </Button>
        </div>
      )}

      <Modal
        visible={showWarning}
        title="确认要交卷吗?"
        onClose={() => setShowWarning(false)}
      >
        <div className={styles.warning}>
          <div className={styles.tips}>你还有题目未完成</div>
          <Button variant="filled" onClick={() => submit()}>
            {submitting ? <Loading /> : "确认交卷"}
          </Button>
        </div>
      </Modal>

      <LogListModal
        visible={logListVisible}
        examId={exam.id}
        onClose={() => setLogListVisible(false)}
      />
    </>
  );
};

export default RightPanel;
